import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BRAND_LOGO_ALT, ORGANIZATION_NAME, brandLogo } from '../branding';
import signature from '../Images/signature.png';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { app } from '../firebase';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

function CertificateComp() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId') || '';
  const fullName = searchParams.get('name') || 'Student Name';
  const courseName = searchParams.get('course') || 'Program Name';
  const cohort = searchParams.get('cohort') || '—';
  const duration = searchParams.get('duration') || '—';
  const certificatePdfRef = useRef(null);
  const [screenScale, setScreenScale] = useState(1);
  const [eligibility, setEligibility] = useState({
    loading: true,
    allowed: false,
    status: '',
    reason: ''
  });
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [qrSrc, setQrSrc] = useState('');
  const [completionDate, setCompletionDate] = useState(null);

  const A4_WIDTH_MM = 297;
  const A4_HEIGHT_MM = 210;
  const MM_TO_PX = 3.7795275591;
  const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX;
  const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX;

  useEffect(() => {
    const updateScale = () => {
      const horizontalPadding = 32;
      const verticalPadding = 100;
      const availableWidth = window.innerWidth - horizontalPadding;
      const availableHeight = window.innerHeight - verticalPadding;
      const rawScale = Math.min(1, availableWidth / A4_WIDTH_PX, availableHeight / A4_HEIGHT_PX);
      const boostedScale = Math.min(1, rawScale * 1.08);
      setScreenScale(Number.isFinite(boostedScale) ? Math.max(0.3, boostedScale) : 1);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [A4_WIDTH_PX, A4_HEIGHT_PX]);

  useEffect(() => {
    let isMounted = true;
    const checkEligibility = async () => {
      if (!userId) {
        if (isMounted) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'missing_user' });
        }
        return;
      }

      try {
        const db = getFirestore(app);
        const q = query(collection(db, 'users'), where('userId', '==', userId));
        const snap = await getDocs(q);
        if (!isMounted) return;
        if (snap.empty) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'not_found' });
          return;
        }

        const userData = snap.docs[0].data();
        const status = String(userData.userStatus || '').trim();
        const allowed = status === 'Completed';
        setAdmissionNumber(userData.admissionNumber || '');
        setCompletionDate(userData.graduatedTime || null);
        setEligibility({
          loading: false,
          allowed,
          status,
          reason: allowed ? '' : 'not_graduated'
        });
      } catch (error) {
        console.error('Error checking certificate eligibility:', error);
        if (isMounted) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'error' });
        }
      }
    };

    checkEligibility();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const normalizeDateValue = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (typeof dateValue === 'number') return new Date(dateValue);
    if (typeof dateValue === 'string') return new Date(dateValue);
    if (typeof dateValue === 'object' && typeof dateValue.seconds === 'number') {
      return new Date(dateValue.seconds * 1000);
    }
    const fallback = new Date(dateValue);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const formatCompletionDate = (dateValue) => {
    const date = normalizeDateValue(dateValue);
    if (!date || Number.isNaN(date.getTime())) return '—';
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const suffix = day >= 11 && day <= 13 ? 'th' : (day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th');
    return `${month} ${day}${suffix}, ${year}`;
  };

  const formatStudentName = (value) => (
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/(^|[\s'-])([a-z])/g, (match, prefix, char) => `${prefix}${char.toUpperCase()}`)
  );

  const displayName = formatStudentName(fullName) || 'Student Name';

  const verifyId = admissionNumber || userId || '';
  const verifyUrl = verifyId
    ? `https://verify.ubunifucollege.com/certificates/${encodeURIComponent(verifyId)}`
    : '';

  useEffect(() => {
    let isMounted = true;
    const toDataUrl = async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch QR');
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const buildQr = async () => {
      if (!verifyUrl) {
        if (isMounted) setQrSrc('');
        return;
      }
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(verifyUrl)}`;
      try {
        const dataUrl = await toDataUrl(qrUrl);
        if (isMounted) setQrSrc(dataUrl);
      } catch (error) {
        if (isMounted) setQrSrc(qrUrl);
      }
    };

    buildQr();
    return () => {
      isMounted = false;
    };
  }, [verifyUrl]);

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const handleDownloadPdf = async () => {
    if (!eligibility.allowed) return;
    if (!certificatePdfRef.current) return;
    const safeName = displayName.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const filename = `Certificate_${safeName || 'Student'}.pdf`;

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    const images = certificatePdfRef.current.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map((img) => (
        img.complete ? Promise.resolve() : new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        })
      ))
    );

    const canvas = await html2canvas(certificatePdfRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.98);
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = A4_WIDTH_MM;
    const pdfHeight = A4_HEIGHT_MM;
    const imgAspect = canvas.width / canvas.height;
    let renderWidth = pdfWidth;
    let renderHeight = pdfWidth / imgAspect;

    if (renderHeight > pdfHeight) {
      renderHeight = pdfHeight;
      renderWidth = pdfHeight * imgAspect;
    }

    const offsetX = (pdfWidth - renderWidth) / 2;
    const offsetY = (pdfHeight - renderHeight) / 2;

    pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderWidth, renderHeight, undefined, 'FAST');
    pdf.save(filename);
  };

  const renderCertificateInner = () => (
    <>
      <div className="certArc certArcTop"></div>
      <div className="certArc certArcBottom"></div>

      <div className="certContent">
        <div className="certBrand">
          <img src={brandLogo} alt={BRAND_LOGO_ALT} className="certLogo" />
          <p className="certBrandLine">{ORGANIZATION_NAME.toUpperCase()}</p>
          <p className="certBrandLine certBrandLineSmall">CERTIFIED ACADEMIC ACHIEVEMENT</p>
        </div>

        <h2 className="certTitle">CERTIFICATE OF COMPLETION</h2>
        <p className="certSubTitle">WE PROUDLY PRESENT THIS CERTIFICATE TO</p>

        <div className="certNameWrap">
          <span className="certName">{displayName}</span>
        </div>

        <p className="certBodyText">For successfully completing a course in</p>
        <p className="certCourse">{courseName}</p>
        <p className="certDate">Completion date: {formatCompletionDate(completionDate)}</p>

        {verifyUrl && (
          <div className="certVerifyRow">
            <div className="certVerifyInfo">
              <p className="certVerifyLabel">Verify certificate</p>
              <p className="certVerifyUrl">{verifyUrl}</p>
              <p className="certVerifyId">Admission No: {verifyId || '—'}</p>
            </div>
            {qrSrc && <img src={qrSrc} alt="Verification QR code" className="certVerifyQr" />}
          </div>
        )}

        <div className="certSignRow certSignRowSingle">
          <div className="certSignBlock certSignBlockCenter">
            <img src={signature} alt="Signature" className="certSignature" />
            <div className="certSignLine"></div>
            <p className="certSignName">REINHARD</p>
            <p className="certSignRole">DIRECTOR</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="certificatePage">
      <div className="certificateToolbar">
        <button className="btn btn-outline-dark" onClick={handleBack}>Back</button>
        <button className="btn btn-dark" onClick={handleDownloadPdf} disabled={!eligibility.allowed}>
          Download PDF
        </button>
      </div>

      {eligibility.loading ? (
        <div className="certificateCanvas">
          <div className="certificateScaleWrap">
            <div className="certificateBorder">
              <p style={{ textAlign: 'center' }}>Checking graduation status...</p>
            </div>
          </div>
        </div>
      ) : eligibility.allowed ? (
        <>
          <div className="certificateCanvas">
            <div
              className="certificateScaleWrap"
              style={{
                width: `${A4_WIDTH_PX * screenScale}px`,
                height: `${A4_HEIGHT_PX * screenScale}px`
              }}
            >
              <div
                className="certificateBorder"
                style={{
                  transform: `scale(${screenScale})`,
                  transformOrigin: 'top left'
                }}
              >
                {renderCertificateInner()}
              </div>
            </div>
          </div>
          <div className="certificatePdfStage">
            <div className="certificateBorder" ref={certificatePdfRef}>
              {renderCertificateInner()}
            </div>
          </div>
        </>
      ) : (
        <div className="certificateCanvas">
          <div className="certificateScaleWrap">
            <div className="certificateBorder">
              <h4 style={{ textAlign: 'center' }}>Certificate Unavailable</h4>
              <p style={{ textAlign: 'center' }}>
                This certificate is available only for graduated students.
              </p>
              {eligibility.status && (
                <p style={{ textAlign: 'center' }}>Current status: {eligibility.status}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificateComp;
